import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Lock, MessageCircle, FileText, Users, Phone, 
  Mail, Clock, CheckCircle, AlertCircle, Send,
  User, ChevronDown, X, RefreshCw, BarChart3, Eye, MapPin, Globe, Search,
  Plus, Trash2, Edit, Image, GripVertical, Bot,
  Upload as UploadIcon
} from 'lucide-react';
import axios from 'axios';
import { InventoryToggle } from '../components/InventoryToggle';
import { DeliveriesAdmin } from '../components/DeliveriesAdmin';
import { AnalyticsKpis } from '../components/analytics/AnalyticsKpis';

const API = '/api';

// Enable cookie-based auth for all requests
axios.defaults.withCredentials = true;

const AdminDashboard = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [creditAnalyses, setCreditAnalyses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [chatLeads, setChatLeads] = useState([]);
  const [incompleteChats, setIncompleteChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  
  // Team management states
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [teamForm, setTeamForm] = useState({
    name: '',
    role_en: '',
    role_fr: '',
    image_url: '',
    order: 0
  });
  
  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [smsText, setSmsText] = useState('');
  
  // Source filter state
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sendingReport, setSendingReport] = useState(false);

  // Send test daily report
  const sendTestReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/admin/send-test-report`);
      toast({ title: 'Daily report sent!', description: 'Check your email for the analytics report.' });
    } catch (error) {
      toast({ title: 'Failed to send report', variant: 'destructive' });
    } finally {
      setSendingReport(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data) {
        setIsAuthenticated(true);
        toast({ title: 'Login successful' });
        fetchAllData();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid credentials';
      toast({ title: typeof msg === 'string' ? msg : 'Invalid credentials', variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setIsAuthenticated(false);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes, convRes, loansRes, creditRes, contactsRes, analystsRes, teamRes, chatLeadsRes, incompleteRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/conversations`),
        axios.get(`${API}/loan-requests`),
        axios.get(`${API}/credit-analysis`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/admin/analysts`),
        axios.get(`${API}/team`),
        axios.get(`${API}/admin/chat-leads`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/chat-incomplete`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setConversations(convRes.data);
      setLoanRequests(loansRes.data);
      setCreditAnalyses(creditRes.data);
      setContacts(contactsRes.data);
      setAnalysts(analystsRes.data);
      setTeamMembers(teamRes.data || []);
      setChatLeads(chatLeadsRes.data || []);
      setIncompleteChats(incompleteRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`);
        setIsAuthenticated(true);
        fetchAllData();
      } catch {
        setIsAuthenticated(false);
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  const handleAssign = async (analyst) => {
    try {
      await axios.post(`${API}/admin/assign`, {
        conversation_id: selectedItem.id,
        analyst_phone: analyst.phone,
        analyst_name: analyst.name
      });
      toast({ title: `Assigned to ${analyst.name}` });
      setShowAssignModal(false);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to assign', variant: 'destructive' });
    }
  };

  const handleAddNote = async () => {
    try {
      await axios.post(`${API}/admin/notes`, {
        item_id: selectedItem.id,
        item_type: activeTab === 'conversations' ? 'conversation' : 
                   activeTab === 'loans' ? 'loan' :
                   activeTab === 'credit' ? 'credit' : 'contact',
        note: noteText
      });
      toast({ title: 'Note added' });
      setShowNoteModal(false);
      setNoteText('');
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to add note', variant: 'destructive' });
    }
  };

  const handleStatusChange = async (item, newStatus, type) => {
    try {
      await axios.post(`${API}/admin/status`, {
        item_id: item.id,
        item_type: type,
        status: newStatus
      });
      toast({ title: 'Status updated' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleSendSMS = async (phone) => {
    if (!smsText.trim()) return;
    try {
      await axios.post(`${API}/admin/send-sms`, {
        to_phone: phone,
        message: smsText
      });
      toast({ title: 'SMS sent' });
      setSmsText('');
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to send SMS', variant: 'destructive' });
    }
  };

  // Team Management Functions
  const handleOpenTeamModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setTeamForm({
        name: member.name,
        role_en: member.role_en,
        role_fr: member.role_fr,
        image_url: member.image_url,
        order: member.order || 0
      });
    } else {
      setEditingMember(null);
      setTeamForm({
        name: '',
        role_en: '',
        role_fr: '',
        image_url: '',
        order: teamMembers.length
      });
    }
    setShowTeamModal(true);
  };

  const handleSaveTeamMember = async () => {
    if (!teamForm.name || !teamForm.role_en || !teamForm.role_fr) {
      toast({ title: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    try {
      if (editingMember) {
        await axios.put(`${API}/team/${editingMember.id}`, teamForm);
        toast({ title: 'Team member updated' });
      } else {
        await axios.post(`${API}/team`, teamForm);
        toast({ title: 'Team member added' });
      }
      setShowTeamModal(false);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to save team member', variant: 'destructive' });
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast({ title: 'Team member deleted' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Failed to delete team member', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'closed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a5c3b] to-[#0d2e1d]">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a5c3b] to-[#0d2e1d] p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#1a5c3b] rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Dashboard</CardTitle>
            <p className="text-gray-500">Credit Auto Reset</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
              />
              <Button type="submit" className="w-full bg-[#1a5c3b] hover:bg-[#165432]" data-testid="admin-login-btn">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-[112px] md:pt-[148px]">
      {/* Header */}
      <header className="bg-[#1a5c3b] text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-3 justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold !text-white">Admin Dashboard</h1>
              <p className="text-green-200 text-sm">Credit Auto Reset CRM</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <InventoryToggle />
            <Button onClick={fetchAllData} variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 pt-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">SMS Conversations</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_conversations || 0}</p>
                </div>
                <MessageCircle className="w-6 h-6 text-blue-500" />
              </div>
              {stats.new_conversations > 0 && (
                <p className="text-blue-600 text-xs mt-2">+{stats.new_conversations} new</p>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Loan Requests</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_loans || 0}</p>
                </div>
                <FileText className="w-6 h-6 text-green-500" />
              </div>
              {stats.new_loans > 0 && (
                <p className="text-green-600 text-xs mt-2">+{stats.new_loans} pending</p>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Credit Analyses</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_credit_analyses || 0}</p>
                </div>
                <AlertCircle className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Contact Messages</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_contacts || 0}</p>
                </div>
                <Mail className="w-6 h-6 text-orange-500" />
              </div>
              {stats.new_contacts > 0 && (
                <p className="text-orange-600 text-xs mt-2">+{stats.new_contacts} new</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'conversations', label: 'SMS Conversations', icon: MessageCircle },
            { id: 'chatbot', label: 'ChatBot Leads', icon: Bot },
            { id: 'loans', label: 'Loan Requests', icon: FileText },
            { id: 'credit', label: 'Credit Analyses', icon: AlertCircle },
            { id: 'contacts', label: 'Contact Messages', icon: Mail },
            { id: 'team', label: 'Team', icon: Users },
            { id: 'deliveries', label: 'Livraisons', icon: UploadIcon }
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id ? 'bg-[#1a5c3b]' : ''}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Content */}
        <Card>
          <CardContent className="p-0">
            {activeTab === 'deliveries' && <DeliveriesAdmin />}
            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="p-6">
                <AnalyticsKpis />
                {/* Send Report Button */}
                <div className="flex justify-end mb-4">
                  <Button 
                    onClick={sendTestReport} 
                    disabled={sendingReport}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {sendingReport ? 'Sending...' : 'Send Daily Report Now'}
                  </Button>
                </div>
                
                {/* Active Visitors & Today Stats */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-gray-500 text-sm">Active Now</p>
                          <p className="text-3xl font-bold text-gray-900">{analytics?.active_count || 0}</p>
                          <p className="text-gray-400 text-xs">visitors on site</p>
                        </div>
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Eye className="w-5 h-5 text-emerald-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-gray-500 text-sm">Today's Page Views</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.today_page_views || 0}</p>
                        </div>
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-blue-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-gray-500 text-sm">Today's Form Leads</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.today_loans || 0}</p>
                        </div>
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-gray-500 text-sm">Today's SMS Leads</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.today_sms || 0}</p>
                        </div>
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <MessageCircle className="w-5 h-5 text-purple-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Weekly Chart */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#1a5c3b]" />
                      Last 7 Days Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics?.daily_stats?.slice().reverse().map((day, idx) => {
                        const maxViews = Math.max(...(analytics?.daily_stats?.map(d => d.page_views) || [1]));
                        const maxLeads = Math.max(...(analytics?.daily_stats?.map(d => d.loans + d.sms) || [1]));
                        return (
                          <div key={idx} className="grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-gray-600">
                                {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                            <div className="col-span-5">
                              <div className="flex items-center gap-2">
                                <div className="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden">
                                  <div 
                                    className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                    style={{ width: `${Math.max((day.page_views / maxViews) * 100, 10)}%` }}
                                  >
                                    <span className="text-xs text-white font-medium">{day.page_views}</span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 w-16">views</span>
                              </div>
                            </div>
                            <div className="col-span-5">
                              <div className="flex items-center gap-2">
                                <div className="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden flex">
                                  <div 
                                    className="bg-[#1a5c3b] h-6 flex items-center justify-end pr-1 transition-all duration-500"
                                    style={{ width: `${maxLeads > 0 ? Math.max((day.loans / maxLeads) * 50, day.loans > 0 ? 15 : 0) : 0}%` }}
                                  >
                                    {day.loans > 0 && <span className="text-xs text-white font-medium">{day.loans}</span>}
                                  </div>
                                  <div 
                                    className="bg-purple-500 h-6 flex items-center justify-end pr-1 transition-all duration-500"
                                    style={{ width: `${maxLeads > 0 ? Math.max((day.sms / maxLeads) * 50, day.sms > 0 ? 15 : 0) : 0}%` }}
                                  >
                                    {day.sms > 0 && <span className="text-xs text-white font-medium">{day.sms}</span>}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 w-16">leads</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-6 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-sm text-gray-600">Page Views</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-[#1a5c3b] rounded"></div>
                        <span className="text-sm text-gray-600">Form Leads</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-purple-500 rounded"></div>
                        <span className="text-sm text-gray-600">SMS Leads</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Lead Source & Popular Pages */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Total Lead Source Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">Form Submissions</span>
                            <span className="text-sm font-bold text-[#1a5c3b]">{analytics?.totals?.loans || 0}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                              className="bg-[#1a5c3b] h-4 rounded-full transition-all duration-500" 
                              style={{ 
                                width: `${analytics?.totals?.loans && analytics?.totals?.sms_chats 
                                  ? (analytics.totals.loans / (analytics.totals.loans + analytics.totals.sms_chats) * 100) 
                                  : analytics?.totals?.loans > 0 ? 100 : 50}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">SMS Chats</span>
                            <span className="text-sm font-bold text-purple-600">{analytics?.totals?.sms_chats || 0}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                              className="bg-purple-500 h-4 rounded-full transition-all duration-500" 
                              style={{ 
                                width: `${analytics?.totals?.loans && analytics?.totals?.sms_chats 
                                  ? (analytics.totals.sms_chats / (analytics.totals.loans + analytics.totals.sms_chats) * 100) 
                                  : analytics?.totals?.sms_chats > 0 ? 100 : 50}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                        <div className="pt-4 border-t mt-4">
                          <div className="flex justify-between">
                            <span className="font-semibold">Total Leads</span>
                            <span className="font-bold text-lg">{(analytics?.totals?.loans || 0) + (analytics?.totals?.sms_chats || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Popular Pages Today</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.page_stats?.slice(0, 6).map((page, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">{idx + 1}</span>
                              <span className="text-sm font-medium">{page.page || '/'}</span>
                            </div>
                            <span className="text-sm font-bold text-[#1a5c3b]">{page.views} views</span>
                          </div>
                        ))}
                        {(!analytics?.page_stats || analytics.page_stats.length === 0) && (
                          <p className="text-gray-400 text-sm text-center py-4">No page views yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Traffic Sources */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-500" />
                        Traffic Sources Today
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.traffic_sources_today?.slice(0, 8).map((source, idx) => {
                          const maxCount = Math.max(...(analytics?.traffic_sources_today?.map(s => s.count) || [1]));
                          const getSourceIcon = (src, medium) => {
                            if (medium === 'cpc') return '💰';
                            if (src === 'google') return '🔍';
                            if (src === 'facebook' || src === 'instagram') return '📱';
                            if (src === 'direct') return '🎯';
                            if (src === 'twitter') return '🐦';
                            if (src === 'linkedin') return '💼';
                            if (src === 'bing') return '🔎';
                            return '🔗';
                          };
                          const getSourceColor = (medium) => {
                            if (medium === 'cpc') return 'bg-orange-500';
                            if (medium === 'organic') return 'bg-green-500';
                            if (medium === 'social') return 'bg-blue-500';
                            if (medium === 'referral') return 'bg-purple-500';
                            return 'bg-gray-500';
                          };
                          const isPaid = source.medium === 'cpc';
                          return (
                            <div key={idx} className={`flex items-center gap-3 py-2 ${isPaid ? 'bg-orange-50 rounded-lg px-2 border border-orange-200' : ''}`}>
                              <span className="text-lg">{getSourceIcon(source.source, source.medium)}</span>
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="text-sm font-medium capitalize flex items-center gap-1">
                                    {source.source}
                                    {isPaid && <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded font-bold">PAID</span>}
                                  </span>
                                  <span className="text-sm text-gray-500">{source.count} visits</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                  <div 
                                    className={`${getSourceColor(source.medium)} h-2 rounded-full transition-all duration-500`}
                                    style={{ width: `${(source.count / maxCount) * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-400 capitalize">{source.medium}</span>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.traffic_sources_today || analytics.traffic_sources_today.length === 0) && (
                          <p className="text-gray-400 text-sm text-center py-4">No traffic data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Globe className="w-5 h-5 text-purple-500" />
                        All-Time Traffic Sources
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.traffic_sources_all_time?.slice(0, 8).map((source, idx) => {
                          const total = analytics?.traffic_sources_all_time?.reduce((sum, s) => sum + s.count, 0) || 1;
                          const percentage = ((source.count / total) * 100).toFixed(1);
                          const isPaid = source.medium === 'cpc';
                          const getSourceColor = (src, medium) => {
                            if (medium === 'cpc') return 'text-orange-600 bg-orange-100 border border-orange-300';
                            if (src === 'google') return 'text-green-600 bg-green-100';
                            if (src === 'facebook') return 'text-blue-600 bg-blue-100';
                            if (src === 'instagram') return 'text-pink-600 bg-pink-100';
                            if (src === 'direct') return 'text-gray-600 bg-gray-100';
                            if (src === 'twitter') return 'text-sky-600 bg-sky-100';
                            if (src === 'bing') return 'text-teal-600 bg-teal-100';
                            return 'text-purple-600 bg-purple-100';
                          };
                          return (
                            <div key={idx} className={`flex items-center justify-between py-2 border-b last:border-0 ${isPaid ? 'bg-orange-50 rounded px-2 -mx-2' : ''}`}>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${getSourceColor(source.source, source.medium)}`}>
                                  {source.source}
                                  {isPaid && ' 💰'}
                                </span>
                                <span className="text-xs text-gray-400 capitalize">{source.medium}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-bold">{source.count}</span>
                                <span className="text-xs text-gray-400 ml-1">({percentage}%)</span>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.traffic_sources_all_time || analytics.traffic_sources_all_time.length === 0) && (
                          <p className="text-gray-400 text-sm text-center py-4">No traffic data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Keywords Section */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Search className="w-5 h-5 text-amber-500" />
                        Keywords Today (Paid Ads)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.keywords_today?.slice(0, 10).map((kw, idx) => {
                          const maxCount = Math.max(...(analytics?.keywords_today?.map(k => k.count) || [1]));
                          return (
                            <div key={idx} className="flex items-center gap-3 py-2">
                              <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-600">{idx + 1}</span>
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="text-sm font-medium">{kw.keyword}</span>
                                  <span className="text-sm font-bold text-amber-600">{kw.count}</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                  <div 
                                    className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(kw.count / maxCount) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.keywords_today || analytics.keywords_today.length === 0) && (
                          <div className="text-center py-6 text-gray-400">
                            <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No keyword data today</p>
                            <p className="text-xs mt-1">Set up utm_term in your ads to track keywords</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Search className="w-5 h-5 text-purple-500" />
                        All-Time Top Keywords
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.keywords_all_time?.slice(0, 10).map((kw, idx) => {
                          const total = analytics?.keywords_all_time?.reduce((sum, k) => sum + k.count, 0) || 1;
                          const percentage = ((kw.count / total) * 100).toFixed(1);
                          return (
                            <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">{idx + 1}</span>
                                <span className="text-sm font-medium">{kw.keyword}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-bold text-purple-600">{kw.count}</span>
                                <span className="text-xs text-gray-400 ml-1">({percentage}%)</span>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.keywords_all_time || analytics.keywords_all_time.length === 0) && (
                          <div className="text-center py-6 text-gray-400">
                            <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No keyword data yet</p>
                            <p className="text-xs mt-1">Keywords from paid campaigns will appear here</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* City Analytics (Canada) */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-red-500" />
                        Canadian Cities Today
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.cities_today?.slice(0, 8).map((city, idx) => {
                          const maxCount = Math.max(...(analytics?.cities_today?.map(c => c.visits) || [1]));
                          return (
                            <div key={idx} className="flex items-center gap-3 py-2">
                              <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-xs font-bold text-red-600">{idx + 1}</span>
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="text-sm font-medium">{city.city}</span>
                                  <span className="text-sm text-gray-500">{city.visits} visits</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                  <div 
                                    className="bg-red-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(city.visits / maxCount) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.cities_today || analytics.cities_today.length === 0) && (
                          <div className="text-center py-6 text-gray-400">
                            <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No city data yet</p>
                            <p className="text-xs mt-1">Visitor locations will appear here</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-blue-500" />
                        All-Time Canadian Cities
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.cities_all_time?.slice(0, 10).map((city, idx) => {
                          const total = analytics?.cities_all_time?.reduce((sum, c) => sum + c.visits, 0) || 1;
                          const percentage = ((city.visits / total) * 100).toFixed(1);
                          return (
                            <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">{idx + 1}</span>
                                <span className="text-sm font-medium">{city.city}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-bold text-blue-600">{city.visits}</span>
                                <span className="text-xs text-gray-400 ml-1">({percentage}%)</span>
                              </div>
                            </div>
                          );
                        })}
                        {(!analytics?.cities_all_time || analytics.cities_all_time.length === 0) && (
                          <div className="text-center py-6 text-gray-400">
                            <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No city data yet</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Conversion Analytics */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-green-500" />
                        Leads by Traffic Source
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analytics?.conversion_by_source?.length > 0 ? (
                          analytics.conversion_by_source.map((source, idx) => {
                            const maxLeads = Math.max(...(analytics?.conversion_by_source?.map(s => s.leads) || [1]));
                            const colors = ['bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];
                            return (
                              <div key={idx} className="flex items-center gap-3">
                                <div className="w-24 text-sm font-medium truncate">{source.source}</div>
                                <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                                  <div 
                                    className={`${colors[idx % colors.length]} h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                                    style={{ width: `${Math.max((source.leads / maxLeads) * 100, 15)}%` }}
                                  >
                                    <span className="text-xs text-white font-bold">{source.leads}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-6 text-gray-400">
                            <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No conversion data yet</p>
                            <p className="text-xs mt-1">Shows which traffic sources generate leads</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-emerald-500" />
                        Where Leads Come From
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analytics?.lead_cities?.length > 0 ? (
                          analytics.lead_cities.map((city, idx) => {
                            const total = analytics?.lead_cities?.reduce((sum, c) => sum + c.leads, 0) || 1;
                            const percentage = ((city.leads / total) * 100).toFixed(1);
                            return (
                              <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600">{idx + 1}</span>
                                  <span className="text-sm font-medium">{city.city}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-bold text-emerald-600">{city.leads} leads</span>
                                  <span className="text-xs text-gray-400 ml-1">({percentage}%)</span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-6 text-gray-400">
                            <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No lead location data yet</p>
                            <p className="text-xs mt-1">Cities where your leads are located</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Active Visitors List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="w-5 h-5 text-green-500" />
                      Active Visitors Right Now
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full animate-pulse">
                        {analytics?.active_count || 0} online
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.active_visitors?.length > 0 ? (
                      <div className="grid md:grid-cols-2 gap-3">
                        {analytics.active_visitors.map((visitor, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                              <div>
                                <p className="text-sm font-medium">Visitor #{visitor.session_id}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {visitor.ip}
                                </p>
                                <p className="text-xs text-blue-500 capitalize">{visitor.traffic_source} / {visitor.traffic_medium}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-[#1a5c3b]">{visitor.current_page}</p>
                              <p className="text-xs text-gray-400">viewing now</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No active visitors at the moment</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* SMS Conversations Tab */}
            {activeTab === 'conversations' && (
              <div className="divide-y">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No SMS conversations yet</p>
                    <p className="text-sm">Conversations will appear when customers text your BDC number</p>
                  </div>
                ) : (
                  conversations.map(conv => (
                    <div key={conv.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1a5c3b] rounded-full flex items-center justify-center">
                            <Phone className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold">{conv.customer_phone}</p>
                            <p className="text-sm text-gray-500">{conv.messages?.length || 0} messages</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conv.status)}`}>
                            {conv.status}
                          </span>
                          {conv.assigned_to && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                              {conv.assigned_to}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Messages Preview */}
                      <div className="ml-13 pl-10 space-y-1 mb-3">
                        {conv.messages?.slice(-3).map((msg, idx) => (
                          <div key={idx} className={`text-sm p-2 rounded ${msg.direction === 'inbound' ? 'bg-gray-100' : 'bg-green-50'}`}>
                            <span className="text-xs text-gray-500">{msg.direction === 'inbound' ? 'Customer' : 'You'}:</span>
                            <p>{msg.body}</p>
                          </div>
                        ))}
                      </div>
                      
                      {/* Reply Box */}
                      <div className="ml-13 pl-10 flex gap-2 mb-3">
                        <Input 
                          placeholder="Type a reply..." 
                          value={selectedItem?.id === conv.id ? smsText : ''}
                          onChange={(e) => {
                            setSelectedItem(conv);
                            setSmsText(e.target.value);
                          }}
                          className="flex-1"
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleSendSMS(conv.customer_phone)}
                          className="bg-[#1a5c3b]"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Actions */}
                      <div className="ml-13 pl-10 flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(conv); setShowAssignModal(true); }}>
                          <User className="w-4 h-4 mr-1" /> Assign
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(conv); setShowNoteModal(true); }}>
                          Add Note
                        </Button>
                        <select 
                          className="text-sm border rounded px-2 py-1"
                          value={conv.status}
                          onChange={(e) => handleStatusChange(conv, e.target.value, 'conversation')}
                        >
                          <option value="new">New</option>
                          <option value="assigned">Assigned</option>
                          <option value="in_progress">In Progress</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      
                      {conv.notes && (
                        <div className="ml-13 pl-10 mt-2 p-2 bg-yellow-50 rounded text-sm">
                          <strong>Notes:</strong> {conv.notes}
                        </div>
                      )}
                      
                      <p className="ml-13 pl-10 text-xs text-gray-400 mt-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(conv.updated_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Loan Requests Tab */}
            {activeTab === 'loans' && (
              <div>
                {/* Source Filter */}
                <div className="p-4 border-b bg-gray-50 flex items-center gap-4">
                  <Label className="text-sm font-medium">Filter by Source:</Label>
                  <select 
                    className="text-sm border rounded px-3 py-2 bg-white"
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                  >
                    <option value="all">All Sources</option>
                    <option value="Website">Website</option>
                    <option value="Website SMS">Website SMS</option>
                  </select>
                  <span className="text-sm text-gray-500">
                    Showing {sourceFilter === 'all' 
                      ? loanRequests.length 
                      : loanRequests.filter(l => (l.source || 'Website') === sourceFilter).length
                    } leads
                  </span>
                </div>
                <div className="divide-y">
                {loanRequests.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No loan requests yet</p>
                  </div>
                ) : (
                  loanRequests
                    .filter(loan => sourceFilter === 'all' || (loan.source || 'Website') === sourceFilter)
                    .map(loan => (
                    <div key={loan.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{loan.firstName} {loan.lastName}</p>
                          <p className="text-sm text-gray-500">{loan.email}</p>
                          <p className="text-sm text-gray-500">{loan.phone}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(loan.status)}`}>
                            {loan.status}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            loan.source === 'Website SMS' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {loan.source || 'Website'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3">
                        <div><span className="text-gray-500">Vehicle:</span> {loan.vehicleType}</div>
                        <div><span className="text-gray-500">Budget:</span> {loan.budgetRange}</div>
                        <div><span className="text-gray-500">Employment:</span> {loan.employmentStatus}</div>
                        <div><span className="text-gray-500">Income:</span> {loan.annualIncome}</div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(loan); setShowNoteModal(true); }}>
                          Add Note
                        </Button>
                        <select 
                          className="text-sm border rounded px-2 py-1"
                          value={loan.status}
                          onChange={(e) => handleStatusChange(loan, e.target.value, 'loan')}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="approved">Approved</option>
                          <option value="declined">Declined</option>
                          <option value="completed">Completed</option>
                        </select>
                        <a href={`sms:${loan.phone}`} className="inline-flex items-center px-3 py-1 bg-[#1a5c3b] text-white text-sm rounded hover:bg-[#165432]">
                          <MessageCircle className="w-4 h-4 mr-1" /> SMS
                        </a>
                        <a href={`tel:${loan.phone}`} className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                          <Phone className="w-4 h-4 mr-1" /> Call
                        </a>
                      </div>
                      {loan.notes && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
                          <strong>Notes:</strong> {loan.notes}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(loan.created_at)}
                      </p>
                    </div>
                  ))
                )}
                </div>
              </div>
            )}

            {/* Credit Analyses Tab */}
            {activeTab === 'credit' && (
              <div className="divide-y">
                {creditAnalyses.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No credit analyses yet</p>
                  </div>
                ) : (
                  creditAnalyses.map(analysis => (
                    <div key={analysis.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{analysis.firstName} {analysis.lastName}</p>
                          <p className="text-sm text-gray-500">{analysis.email}</p>
                          <p className="text-sm text-gray-500">{analysis.phone}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(analysis); setShowNoteModal(true); }}>
                          Add Note
                        </Button>
                        <select 
                          className="text-sm border rounded px-2 py-1"
                          value={analysis.status}
                          onChange={(e) => handleStatusChange(analysis, e.target.value, 'credit')}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                        <a href={`sms:${analysis.phone}`} className="inline-flex items-center px-3 py-1 bg-[#1a5c3b] text-white text-sm rounded hover:bg-[#165432]">
                          <MessageCircle className="w-4 h-4 mr-1" /> SMS
                        </a>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(analysis.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Contact Messages Tab */}
            {activeTab === 'contacts' && (
              <div className="divide-y">
                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No contact messages yet</p>
                  </div>
                ) : (
                  contacts.map(contact => (
                    <div key={contact.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{contact.name}</p>
                          <p className="text-sm text-gray-500">{contact.email}</p>
                          {contact.phone && <p className="text-sm text-gray-500">{contact.phone}</p>}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                          {contact.status}
                        </span>
                      </div>
                      <p className="text-sm bg-gray-50 p-3 rounded mb-3">{contact.message}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(contact); setShowNoteModal(true); }}>
                          Add Note
                        </Button>
                        <select 
                          className="text-sm border rounded px-2 py-1"
                          value={contact.status}
                          onChange={(e) => handleStatusChange(contact, e.target.value, 'contact')}
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        {contact.phone && (
                          <a href={`sms:${contact.phone}`} className="inline-flex items-center px-3 py-1 bg-[#1a5c3b] text-white text-sm rounded hover:bg-[#165432]">
                            <MessageCircle className="w-4 h-4 mr-1" /> SMS
                          </a>
                        )}
                        <a href={`mailto:${contact.email}`} className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                          <Mail className="w-4 h-4 mr-1" /> Email
                        </a>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(contact.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Team Management Tab */}
            {activeTab === 'team' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
                    <p className="text-sm text-gray-500">Manage your team displayed on the website</p>
                  </div>
                  <Button onClick={() => handleOpenTeamModal()} className="bg-[#1a5c3b] hover:bg-[#165432]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </div>

                {teamMembers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No team members yet</p>
                    <Button onClick={() => handleOpenTeamModal()} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Team Member
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teamMembers.map((member, index) => (
                      <Card key={member.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="relative h-48 bg-gray-100">
                          {member.image_url ? (
                            <img 
                              src={member.image_url} 
                              alt={member.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=1a5c3b&color=fff&size=200`;
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#1a5c3b]">
                              <span className="text-4xl font-bold text-white">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          )}
                          <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                            #{index + 1}
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-bold text-lg text-gray-900">{member.name}</h3>
                          <p className="text-sm text-[#1a5c3b] mb-1">{member.role_en}</p>
                          <p className="text-sm text-gray-500 mb-4">{member.role_fr}</p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleOpenTeamModal(member)}
                              className="flex-1"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleDeleteTeamMember(member.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ChatBot Leads Tab */}
            {activeTab === 'chatbot' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">ChatBot Leads</h2>
                    <p className="text-sm text-gray-500">{chatLeads.length} completed, {incompleteChats.length} incomplete</p>
                  </div>
                  <Button onClick={fetchAllData} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {/* Completed Leads */}
                <h3 className="font-semibold text-gray-800 mb-3">Completed Leads</h3>
                {chatLeads.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg mb-6">
                    <Bot className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No completed chatbot leads yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-8">
                    {chatLeads.map((lead, i) => {
                      const ext = lead.extracted_data || {};
                      const rating = lead.lead_rating || 'N/A';
                      const ratingColor = rating === 'Hot' ? 'bg-red-100 text-red-700' : rating === 'Warm' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
                      return (
                        <div key={i} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" data-testid={`chat-lead-${i}`}
                          onClick={() => setSelectedChat(selectedChat?.session_id === lead.session_id ? null : lead)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#1a5c3b] rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {(ext.nom || 'N')[0]}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{ext.nom || 'N/A'}</p>
                                <p className="text-sm text-gray-500">{ext.telephone || ''} {ext.courriel ? `| ${ext.courriel}` : ''}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${ratingColor}`}>{rating} {lead.lead_score ? `(${lead.lead_score})` : ''}</span>
                              <span className="text-xs text-gray-400">{lead.source}</span>
                            </div>
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span>{ext.employeur ? `Emploi: ${ext.employeur}` : ''}</span>
                            <span>{ext.salaire ? `Salaire: ${ext.salaire}` : ''}</span>
                            <span>{ext.budget_mensuel ? `Budget: ${ext.budget_mensuel}` : ''}</span>
                            <span>{ext.vehicule_recherche ? `Véhicule: ${ext.vehicule_recherche}` : ''}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{new Date(lead.created_at).toLocaleString('fr-CA')}</p>

                          {/* Expanded detail */}
                          {selectedChat?.session_id === lead.session_id && (
                            <div className="mt-4 border-t pt-4">
                              {ext && (
                                <div className="grid grid-cols-2 gap-2 mb-4">
                                  {Object.entries({
                                    'Nom': ext.nom, 'Date naissance': ext.date_naissance, 'Téléphone': ext.telephone,
                                    'Courriel': ext.courriel, 'Adresse': ext.adresse, 'Ville': ext.ville,
                                    'Code postal': ext.code_postal, 'Logement': ext.statut_logement,
                                    'Paiement mensuel': ext.paiement_mensuel, 'Faillite': ext.faillite,
                                    'État civil': ext.etat_civil, 'Enfants': ext.enfants,
                                    'Employeur': ext.employeur, 'Poste': ext.poste, 'Salaire': ext.salaire,
                                    'Ancienneté': ext.anciennete_emploi, 'Véhicule': ext.vehicule_recherche,
                                    'Budget': ext.budget_mensuel, 'Raison': ext.raison_achat,
                                    'Cosignataire': ext.cosignataire
                                  }).filter(([,v]) => v).map(([k, v]) => (
                                    <div key={k} className="text-xs">
                                      <span className="font-semibold text-gray-600">{k}:</span> <span className="text-gray-800">{v}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {lead.messages && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Conversation</h4>
                                  <div className="bg-gray-50 rounded-lg p-3 max-h-60 overflow-y-auto space-y-2">
                                    {lead.messages.map((msg, j) => (
                                      <div key={j} className={`text-xs ${msg.role === 'user' ? 'text-right' : ''}`}>
                                        <span className={`inline-block px-2 py-1 rounded-lg ${msg.role === 'user' ? 'bg-[#1a5c3b] text-white' : 'bg-white border text-gray-700'}`}>
                                          {msg.content}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Incomplete Chats */}
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                  Incomplete Chats (Follow-up needed)
                </h3>
                {incompleteChats.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No abandoned chats to follow up on</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {incompleteChats.map((chat, i) => (
                      <div key={i} className="border rounded-lg p-3 bg-orange-50/50" data-testid={`incomplete-chat-${i}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-800">Session: {chat.session_id.slice(0, 20)}...</p>
                            <p className="text-xs text-gray-500">{chat.message_count} messages | Last: "{chat.last_message?.slice(0, 50)}"</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-orange-400 rounded-full" style={{width: `${chat.progress}%`}}></div>
                            </div>
                            <span className="text-xs text-gray-500">{chat.progress}%</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{new Date(chat.updated_at).toLocaleString('fr-CA')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Member Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTeamModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label htmlFor="role_en">Role (English) *</Label>
                <Input
                  id="role_en"
                  value={teamForm.role_en}
                  onChange={(e) => setTeamForm({...teamForm, role_en: e.target.value})}
                  placeholder="Sales Manager"
                />
              </div>
              <div>
                <Label htmlFor="role_fr">Role (French) *</Label>
                <Input
                  id="role_fr"
                  value={teamForm.role_fr}
                  onChange={(e) => setTeamForm({...teamForm, role_fr: e.target.value})}
                  placeholder="Directeur des ventes"
                />
              </div>
              <div>
                <Label htmlFor="image_url">Photo URL</Label>
                <Input
                  id="image_url"
                  value={teamForm.image_url}
                  onChange={(e) => setTeamForm({...teamForm, image_url: e.target.value})}
                  placeholder="https://example.com/photo.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste a URL to an image. If left empty, initials will be shown.
                </p>
              </div>
              {teamForm.image_url && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">Preview:</p>
                  <img 
                    src={teamForm.image_url} 
                    alt="Preview" 
                    className="w-24 h-24 object-cover rounded-lg mx-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(teamForm.name || 'Preview')}&background=1a5c3b&color=fff&size=200`;
                    }}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={teamForm.order}
                  onChange={(e) => setTeamForm({...teamForm, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower numbers appear first. Use 0 for President, 1-2 for VPs, etc.
                </p>
              </div>
              <Button onClick={handleSaveTeamMember} className="w-full bg-[#1a5c3b] hover:bg-[#165432]">
                {editingMember ? 'Update Member' : 'Add Member'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Assign to Analyst</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAssignModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {analysts.map(analyst => (
                <Button
                  key={analyst.phone}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleAssign(analyst)}
                >
                  <User className="w-4 h-4 mr-2" />
                  <div className="text-left">
                    <p className="font-medium">{analyst.name}</p>
                    <p className="text-xs text-gray-500">{analyst.role}</p>
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Note</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowNoteModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                className="w-full p-3 border rounded-lg min-h-[100px]"
                placeholder="Enter your note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button onClick={handleAddNote} className="w-full bg-[#1a5c3b]">
                Save Note
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
